import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnyoGridComponent } from 'projects/app-core/src/app/models/AnyoGridComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { GridResponse } from 'projects/app-core/src/models/gridResponse';
import { Observable } from 'rxjs';
import { LyfisService } from '../../../services/lyfis.service';
import { MasterdataService } from '../../../services/masterdata.service';
import {
  LyfisPartnerProfileGridItem,
  LyfisPartnerProfilesDateFilter,
  LyfisPartnerProfilesFilter,
  LyfisPartnerProfilesFilterData,
  LyfisPartnerProfilesGridRequest,
  LyfisPartnerProfilesSort,
} from '../models/lyfis-partner-profiles-grid.models';

@Component({
  selector: 'app-manage-lyfis-partner-profile',
  templateUrl: './manage-lyfis-partner-profile.component.html',
  styleUrl: './manage-lyfis-partner-profile.component.scss',
})
export class ManageLyfisPartnerProfileComponent extends AnyoGridComponent<
  LyfisPartnerProfilesSort,
  LyfisPartnerProfilesFilter,
  string,
  LyfisPartnerProfilesDateFilter,
  LyfisPartnerProfilesGridRequest,
  LyfisPartnerProfilesFilterData,
  LyfisPartnerProfileGridItem
> {
  // filters state
  activeFilter: boolean = true;
  partnerOptions: { label: string; value: string }[] = [];
  diseasesOptions: { label: string; value: string }[] = [];
  selectedPartnerIds: string[] = [];
  partnerTempSelected: string[] = [];

  constructor(
    auth: AuthService,
    router: Router,
    route: ActivatedRoute,
    private lyfisSvc: LyfisService,
    private masterdata: MasterdataService,
  ) {
    super(auth, router, route);
  }

  override async ready(): Promise<void> {
    this.loadPartnerOptions();
    this.loadEnabledDiseasesLabel();
    return super.ready();
  }

  protected override buildRequest(
    exportFlag = false,
  ): LyfisPartnerProfilesGridRequest {
    const req: LyfisPartnerProfilesGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: exportFlag,
      filters: {},
    } as LyfisPartnerProfilesGridRequest;

    // active filter
    if (typeof this.activeFilter === 'boolean') {
      (req.filters as Record<string, string[] | undefined>)[
        LyfisPartnerProfilesFilter.ACTIVE
      ] = [String(this.activeFilter)];
    }

    // partner filter
    if (this.selectedPartnerIds?.length) {
      (req.filters as Record<string, string[] | undefined>)[
        LyfisPartnerProfilesFilter.PARTNER_ID
      ] = [...this.selectedPartnerIds];
    }

    return req;
  }

  protected fetchGridData(
    request: LyfisPartnerProfilesGridRequest,
  ): Observable<
    GridResponse<
      LyfisPartnerProfilesFilterData | undefined,
      LyfisPartnerProfileGridItem
    >
  > {
    return this.lyfisSvc.getPartnerProfilesGrid(request);
  }

  protected override applyRequestToState(
    req: LyfisPartnerProfilesGridRequest,
  ): void {
    super.applyRequestToState(req);
    if (req.filters) {
      const activeArr = (req.filters as Record<string, string[] | undefined>)[
        LyfisPartnerProfilesFilter.ACTIVE
      ];
      if (Array.isArray(activeArr) && activeArr.length) {
        this.activeFilter = activeArr[0] === 'true';
      }
      const partnerArr = (req.filters as Record<string, string[] | undefined>)[
        LyfisPartnerProfilesFilter.PARTNER_ID
      ];
      if (Array.isArray(partnerArr)) {
        this.selectedPartnerIds = [...partnerArr];
        this.partnerTempSelected = [...partnerArr];
      }
    }
  }

  private loadPartnerOptions(): void {
    this.partnerOptions = [{ label: 'Default profiles', value: 'default' }];
    this.masterdata.partnerMasterDataList().subscribe({
      next: (partners) => {
        const mapped = (partners || [])
          .map((p) => ({
            label: p.companyName as string,
            value: p.partnerId as string,
          }))
          .filter((x) => !!x.value && !!x.label);
        this.partnerOptions = [
          { label: 'Default profiles', value: 'default' },
          ...mapped,
        ];
      },
      error: () => {},
    });
  }

  getPartnerLabel(id?: string | null): string {
    if (!id) {
      return 'Default profiles';
    }
    return (
      this.partnerOptions.find((p) => p.value === id)?.label ||
      'Default profiles'
    );
  }

  loadEnabledDiseasesLabel() {
    this.masterdata.lyfisMasterDataList().subscribe({
      next: (res) => {
        this.diseasesOptions =
          res?.LyfisDiseases?.map((d) => ({ label: d.data, value: d.m_id })) ||
          [];
      },
      error: () => {
        this.diseasesOptions = [];
      },
    });
  }
  getEnabledDiseasesLabel(diseases: string[]) {
    return this.diseasesOptions
      .filter((d) => diseases.includes(d.value))
      .map((d) => d.label)
      .join(', ');
  }

  // Active toggle
  onActiveToggle(val: boolean): void {
    this.activeFilter = val;
    this.page = 1;
    this.fetch();
  }

  onPartnerTempChange(values: string[]): void {
    this.partnerTempSelected = values || [];
  }

  applyPartnerFilter(): void {
    this.selectedPartnerIds = [...this.partnerTempSelected];
    this.page = 1;
    this.fetch();
  }

  resetPartnerFilter(): void {
    this.partnerTempSelected = [];
    this.selectedPartnerIds = [];
    this.page = 1;
    this.fetch();
  }

  onEdit(id: string): void {
    this.router.navigate(['/lyfis/create', id]);
  }
}
